
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: 'Home', href: '/' },
    { 
      name: 'Experiences', 
      href: '/experiences',
      submenu: [
        { name: 'All Experiences', href: '/experiences' },
        { name: 'Mountain Retreats', href: '/experiences/mountain' },
        { name: 'Forest Escapes', href: '/experiences/forest' },
        { name: 'Lakeside Adventures', href: '/experiences/lakeside' },
        { name: 'Desert Sanctuaries', href: '/experiences/desert' }
      ]
    },
    { 
      name: 'Investments', 
      href: '/investments',
      submenu: [
        { name: 'Investment Opportunities', href: '/investments' },
        { name: 'Investment Packages', href: '/investment-packages' },
        { name: 'Investment Process', href: '/investments/process' },
        { name: 'Portfolio & Returns', href: '/investments/returns' },
        { name: 'Start Investing', href: '/start-investing' }
      ]
    },
    { name: 'Dealership', href: '/dealership' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/22d69ea1c69f4444f58b39b98de3f50c/80f4d1f39b822a3424440f3dd7d968b8.jpeg" 
              alt="GLAMPIRE Logo" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Home
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              About
            </Link>
            <div className="relative group">
              <button className="text-gray-700 hover:text-emerald-600 transition-colors flex items-center cursor-pointer">
                Experiences
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <Link to="/experiences" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  All Experiences
                </Link>
                <Link to="/experiences/mountain" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Mountain Retreats
                </Link>
                <Link to="/experiences/forest" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Forest Escapes
                </Link>
                <Link to="/experiences/lakeside" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Lakeside Getaways
                </Link>
                <Link to="/experiences/desert" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Desert Adventures
                </Link>
              </div>
            </div>
            <Link to="/retreats" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Retreats
            </Link>
            <div className="relative group">
              <button className="text-gray-700 hover:text-emerald-600 transition-colors flex items-center cursor-pointer">
                Investments
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <Link to="/investments" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Overview
                </Link>
                <Link to="/investment-packages" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Investment Packages
                </Link>
                <Link to="/investments/portfolio" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Portfolio
                </Link>
                <Link to="/investments/returns" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Returns
                </Link>
                <Link to="/investments/process" className="block px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                  Process
                </Link>
              </div>
            </div>
            <Link to="/dealership" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Dealership
            </Link>
            <Link to="/consultation" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Consultation
            </Link>
            <Link
              to="/partnership"
              className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer"
            >
              Partner with Us
            </Link>
            <Link to="/contact" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Contact
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-emerald-600 focus:outline-none cursor-pointer"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="space-y-1">
              <Link to="/" className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                Home
              </Link>
              <Link to="/about" className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                About
              </Link>
              <Link to="/experiences" className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                Experiences
              </Link>
              <Link to="/retreats" className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                Retreats
              </Link>
              <Link to="/investments" className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                Investments
              </Link>
              <Link to="/dealership" className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                Dealership
              </Link>
              <Link to="/consultation" className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                Consultation
              </Link>
              <Link
                to="/partnership"
                className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
              >
                Partner with Us
              </Link>
              <Link to="/contact" className="block px-3 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer">
                Contact
              </Link>
              <Link to="/book" className="block px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                Book Now
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
